// src/HomePage/Game/Werewolf/WerewolfRoomView.js
import React, { useState } from 'react';
import { useWerewolf } from './WerewolfContext';
import './WerewolfRoomView.css';

const WerewolfRoomView = () => {
  const {
    currentRoom,
    lastFinishedGame,
    activeUid,
    leaveRoom,
    toggleReady,
    updateRoomSettings,
    kickPlayer,
    startGame
  } = useWerewolf();

  const [activeModal, setActiveModal] = useState(null); // 'info' | 'rules' | 'roles' | 'view_rules' | 'view_last_logs'
  const [expandedDays, setExpandedDays] = useState({});

  const [editRoomName, setEditRoomName] = useState(currentRoom?.roomName || '');
  const [editPassword, setEditPassword] = useState(currentRoom?.password || '');

  const [gameRules, setGameRules] = useState(currentRoom?.gameRules || {
    dayDuration: 0,
    nightDuration: 0,
    voteDuration: 0,
    revealRoleOnDeath: true
  });

  const [rolesConfig, setRolesConfig] = useState(currentRoom?.rolesConfig || {
    werewolfCount: 2,
    seer: true,
    bodyguard: true,
    witch: true,
    hunter: false,
    cupid: false
  });

  const [copied, setCopied] = useState(false);

  const formatTime = (secs) => secs === 0 ? "Tự do" : `${secs}s`;

  if (!currentRoom) return null;

  const playersMap = currentRoom.players || {};
  const playersList = Object.values(playersMap);
  const isHost = currentRoom.hostUid === activeUid;
  const myPlayer = playersMap[activeUid];

  const currentRules = currentRoom.gameRules || gameRules;
  const currentRoles = currentRoom.rolesConfig || rolesConfig;

  // Lịch sử ván vừa rồi
  const lastLogs = lastFinishedGame?.gameLogs || currentRoom.gameState?.gameLogs || [];
  const groupedLastLogs = lastLogs.reduce((acc, log) => {
    const d = log.day || 1;
    if (!acc[d]) acc[d] = [];
    acc[d].push(log);
    return acc;
  }, {});

  const toggleDayExpand = (dayNum) => {
    setExpandedDays(prev => ({
      ...prev,
      [dayNum]: prev[dayNum] === undefined ? false : !prev[dayNum]
    }));
  };

  // Quản lý Thêm / Xóa ghế của Chủ phòng
  const handleAddSeat = async () => {
    if (!isHost) return;
    if (currentRoom.maxPlayers >= 15) {
      alert("Số lượng ghế tối đa trong phòng là 15!");
      return;
    }
    await updateRoomSettings({ maxPlayers: currentRoom.maxPlayers + 1 });
  };

  const handleRemoveSeat = async () => {
    if (!isHost) return;
    if (currentRoom.maxPlayers <= 4) {
      alert("Số lượng ghế tối thiểu là 4!");
      return;
    }
    if (currentRoom.maxPlayers <= playersList.length) {
      alert("Không thể xóa ghế khi tất cả các ghế đang có người ngồi!");
      return;
    }
    await updateRoomSettings({ maxPlayers: currentRoom.maxPlayers - 1 });
  };

  // Nút 1-Click xóa sạch tất cả ghế trống
  const handleRemoveAllEmptySeats = async () => {
    if (!isHost) return;
    const targetSeatCount = Math.max(4, playersList.length);
    await updateRoomSettings({ maxPlayers: targetSeatCount });
  };

  // KIỂM TRA ĐIỀU KIỆN BẮT ĐẦU GAME
  const minPlayers = 4;
  const isEnoughPlayers = playersList.length >= minPlayers;
  const readyCount = playersList.filter(p => p.isHost || p.isReady).length;
  const emptySeatsCount = Math.max(0, currentRoom.maxPlayers - playersList.length);
  const hasEmptySeats = emptySeatsCount > 0;

  // Điều kiện để nút Bắt Đầu sáng: Đủ 4 người + Tất cả Ready + KHÔNG CÓ GHẾ TRỐNG
  const allReady = isEnoughPlayers && readyCount === playersList.length && !hasEmptySeats;

  // Thống kê vai trò
  const specialRolesCount = (rolesConfig.seer ? 1 : 0) +
    (rolesConfig.bodyguard ? 1 : 0) +
    (rolesConfig.witch ? 1 : 0) +
    (rolesConfig.hunter ? 1 : 0) +
    (rolesConfig.cupid ? 1 : 0);

  const wolvesCount = rolesConfig.werewolfCount || 2;
  const totalSpecialAndWolves = wolvesCount + specialRolesCount;
  const maxP = Number(currentRoom.maxPlayers);
  const calculatedVillagers = Math.max(0, maxP - totalSpecialAndWolves);
  const totalRolesAssigned = totalSpecialAndWolves + calculatedVillagers;
  const isOverLimit = totalSpecialAndWolves > maxP;

  const handleCopyPin = () => {
    navigator.clipboard.writeText(currentRoom.roomCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveInfo = async (e) => {
    e.preventDefault();
    const isLocked = Boolean(editPassword && editPassword.trim());
    const res = await updateRoomSettings({
      roomName: editRoomName,
      isLocked,
      password: editPassword
    });
    if (res?.success) setActiveModal(null);
    else alert("Lỗi cập nhật: " + (res?.message || ''));
  };

  const handleSaveRules = async (e) => {
    e.preventDefault();
    const res = await updateRoomSettings({ gameRules });
    if (res?.success) setActiveModal(null);
    else alert("Lỗi cập nhật: " + (res?.message || ''));
  };

  const handleSaveRoles = async (e) => {
    e.preventDefault();
    if (isOverLimit) {
      alert("⚠️ Số lượng vai trò vượt quá số người chơi!");
      return;
    }
    const res = await updateRoomSettings({ rolesConfig });
    if (res?.success) setActiveModal(null);
    else alert("Lỗi cập nhật: " + (res?.message || ''));
  };

  const seatSlots = Array.from({ length: currentRoom.maxPlayers }, (_, i) => i + 1);

  return (
    <div className="ww-room-container">
      {/* 1. HEADER PHÒNG */}
      <div className="ww-room-header-card">
        <div className="ww-room-title-box">
          <h3 className="ww-room-header-title">{currentRoom.roomName}</h3>
          <div className="ww-room-pin-badge">
            <span>Mã Phòng (PIN): <strong>#{currentRoom.roomCode}</strong></span>
            <button className="ww-copy-btn" onClick={handleCopyPin}>
              {copied ? 'Đã chép! ✔️' : '📋 Sao chép'}
            </button>
          </div>
        </div>

        <div className="ww-room-header-actions">
          {lastLogs.length > 0 && (
            <button className="ww-btn secondary" onClick={() => setActiveModal('view_last_logs')}>
              📜 Lịch Sử Ván Vừa Rồi
            </button>
          )}
          {isHost && (
            <button className="ww-btn primary-glass" onClick={() => setActiveModal('info')}>
              ⚙️ Sửa Phòng
            </button>
          )}
          <button className="ww-btn danger-glass" onClick={leaveRoom}>
            🚪 Rời Phòng
          </button>
        </div>
      </div>

      {/* 2. CẤU HÌNH VAI TRÒ TRỰC QUAN (VISUAL ROLE CARDS) */}
      <div className="ww-card-section visual-roles-section">
        <div className="ww-card-section-header">
          <div className="ww-header-title-group">
            <h4>🎭 Cấu Hình Vai Trò cho {currentRoom.maxPlayers} người chơi</h4>
          </div>
          {isHost ? (
            <button className="ww-btn-action-sm" onClick={() => setActiveModal('roles')}>
              ⚙️ Chỉnh Sửa Vai Trò
            </button>
          ) : (
            <button className="ww-btn-action-sm" onClick={() => setActiveModal('view_rules')}>
              👁️ Chi Tiết Luật Chơi
            </button>
          )}
        </div>
          <span className="ww-bot-notice">🤖 Quản trò tự động</span>

        {/* Thanh Thống Kê Phân Bổ Tỷ Lệ Vai Trò */}
        <div className="ww-role-distribution-bar">
          <div
            className="ww-dist-segment wolf"
            style={{ width: `${(wolvesCount / currentRoom.maxPlayers) * 100}%` }}
            title={`Ma Sói: ${wolvesCount} (${Math.round((wolvesCount / currentRoom.maxPlayers) * 100)}%)`}
          />
          <div
            className="ww-dist-segment special"
            style={{ width: `${(specialRolesCount / currentRoom.maxPlayers) * 100}%` }}
            title={`Chức Năng: ${specialRolesCount} (${Math.round((specialRolesCount / currentRoom.maxPlayers) * 100)}%)`}
          />
          <div
            className="ww-dist-segment villager"
            style={{ width: `${(calculatedVillagers / currentRoom.maxPlayers) * 100}%` }}
            title={`Dân Làng: ${calculatedVillagers} (${Math.round((calculatedVillagers / currentRoom.maxPlayers) * 100)}%)`}
          />
        </div>

        {/* Lưới Thẻ Vai Trò Sinh Động */}
        <div className="ww-visual-roles-grid">
          <div className="ww-visual-role-card werewolf">
            <span className="role-card-icon">🐺</span>
            <div className="role-card-info">
              <span className="role-card-name">Ma Sói</span>
              <span className="role-card-team">Phe Sói</span>
            </div>
            <span className="role-card-count">x{currentRoles.werewolfCount || 2}</span>
          </div>

          {currentRoles.seer && (
            <div className="ww-visual-role-card special">
              <span className="role-card-icon">🔮</span>
              <div className="role-card-info">
                <span className="role-card-name">Tiên Tri</span>
                <span className="role-card-team">Phe Dân</span>
              </div>
              <span className="role-card-count">x1</span>
            </div>
          )}

          {currentRoles.bodyguard && (
            <div className="ww-visual-role-card special">
              <span className="role-card-icon">🛡️</span>
              <div className="role-card-info">
                <span className="role-card-name">Bảo Vệ</span>
                <span className="role-card-team">Phe Dân</span>
              </div>
              <span className="role-card-count">x1</span>
            </div>
          )}

          {currentRoles.witch && (
            <div className="ww-visual-role-card special">
              <span className="role-card-icon">🧪</span>
              <div className="role-card-info">
                <span className="role-card-name">Phù Thủy</span>
                <span className="role-card-team">Phe Dân</span>
              </div>
              <span className="role-card-count">x1</span>
            </div>
          )}

          {currentRoles.hunter && (
            <div className="ww-visual-role-card special">
              <span className="role-card-icon">🏹</span>
              <div className="role-card-info">
                <span className="role-card-name">Thợ Săn</span>
                <span className="role-card-team">Phe Dân</span>
              </div>
              <span className="role-card-count">x1</span>
            </div>
          )}

          {currentRoles.cupid && (
            <div className="ww-visual-role-card special">
              <span className="role-card-icon">💘</span>
              <div className="role-card-info">
                <span className="role-card-name">Thần Tình Yêu</span>
                <span className="role-card-team">Phe Dân</span>
              </div>
              <span className="role-card-count">x1</span>
            </div>
          )}

          <div className="ww-visual-role-card villager">
            <span className="role-card-icon">👨‍🌾</span>
            <div className="role-card-info">
              <span className="role-card-name">Dân Làng</span>
              <span className="role-card-team">Phe Dân</span>
            </div>
            <span className="role-card-count">x{calculatedVillagers}</span>
          </div>
        </div>
      </div>

      {/* 3. KHU VỰC LUẬT CHƠI */}
      <div className="ww-card-section">
        <div className="ww-card-section-header">
          <h4>⏱️ Thời Gian & Luật Chơi</h4>
          {isHost ? (
            <button className="ww-btn-action-sm" onClick={() => setActiveModal('rules')}>
              ⚙️ Sửa Luật Chơi
            </button>
          ) : (
            <button className="ww-btn-action-sm" onClick={() => setActiveModal('view_rules')}>
              📖 Chi Tiết
            </button>
          )}
        </div>

        <div className="ww-roles-pills-list">
          <span className="ww-role-pill timer-pill">
            🌙 Đêm (2 lượt): Mỗi lượt {formatTime(currentRules.nightDuration)}
          </span>
          <span className="ww-role-pill timer-pill">
            ☀️ Ngày (Thảo luận): {formatTime(currentRules.dayDuration)}
          </span>
          <span className="ww-role-pill timer-pill">
            🗳️ Ngày (Bỏ phiếu): {formatTime(currentRules.voteDuration)}
          </span>
          <span className="ww-role-pill timer-pill">
            💀 Lộ Vai Khi Chết: {currentRules.revealRoleOnDeath ? 'CÓ' : 'KHÔNG'}
          </span>
        </div>
      </div>

      {/* 4. SƠ ĐỒ CHỖ NGỒI VÀ THÊM / XÓA GHẾ */}
      <div className="ww-seat-grid-section">
        <div className="ww-seat-grid-header">
          <h4>Sơ Đồ Chỗ Ngồi</h4>
          <div className="ww-seat-header-right">
            <span>👥 {playersList.length}/{currentRoom.maxPlayers} Ghế</span>
          </div>
        </div>

        {/* BANNER CẢNH BÁO GHẾ TRỐNG DÀNH CHO CHỦ PHÒNG */}
        {hasEmptySeats && (
          <div className="ww-empty-seats-alert">
            <div className="empty-alert-info">
              ⚠️ <strong>CẢNH BÁO GHẾ TRỐNG:</strong> Phòng đang thừa <strong>{emptySeatsCount} ghế trống</strong>. Chủ phòng phải bỏ các ghế trống (hoặc chờ đủ người) mới có thể bắt đầu chơi!
            </div>
            {isHost && (
              <button className="ww-btn-action-sm danger" onClick={handleRemoveAllEmptySeats}>
                🧹 Bỏ tất cả {emptySeatsCount} ghế trống
              </button>
            )}
          </div>
        )}

        <div className="ww-seats-grid">
          {seatSlots.map((seatNum) => {
            const occupant = playersList.find(p => p.seatIndex === seatNum);
            const isMe = occupant?.uid === activeUid;

            return (
              <div
                key={seatNum}
                className={`ww-seat-slot ${occupant ? 'occupied' : 'empty'} ${isMe ? 'is-me' : ''} ${occupant?.isReady ? 'is-ready' : ''}`}
              >
                <span className="ww-seat-number">#{seatNum}</span>

                {occupant ? (
                  <>
                    {isHost && !isMe && (
                      <button
                        className="ww-kick-btn"
                        title="Đuổi khỏi phòng"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Bạn có chắc muốn đuổi ${occupant.fullName} khỏi phòng?`)) {
                            kickPlayer(occupant.uid);
                          }
                        }}
                      >
                        ✕
                      </button>
                    )}

                    <div className="ww-slot-avatar">{occupant.avatar || '👤'}</div>

                    <div className="ww-slot-name-box">
                      <span className="ww-slot-holy-name">
                        {occupant.isCatechist && <span className="ww-glv-tag">🛡️ GLV</span>}
                        {occupant.holyName || 'Thiếu nhi'}
                      </span>
                      <span className="ww-slot-full-name">{occupant.fullName}</span>
                    </div>

                    <div className={`ww-slot-ready-tag ${occupant.isHost ? 'host' : occupant.isReady ? 'ready' : 'waiting'}`}>
                      {occupant.isHost ? '👑 Chủ phòng' : occupant.isReady ? '✔️ Sẵn Sàng' : '⏳ Đang chờ'}
                    </div>
                  </>
                ) : (
                  <div className="ww-empty-slot-container">
                    <div className="ww-empty-slot-label">
                      <span>⏳ Ghế trống</span>
                    </div>
                    {isHost && (
                      <button
                        className="ww-delete-seat-btn"
                        title="Xóa ghế trống này"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveSeat();
                        }}
                      >
                        ✕ Xóa
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Nút ô Thêm Ghế Nhanh cho Chủ phòng ở cuối danh sách */}
          {isHost && currentRoom.maxPlayers < 15 && (
            <div className="ww-seat-slot add-seat-slot" onClick={handleAddSeat}>
              <div className="ww-add-slot-label">
                <span>➕ Thêm Ghế</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* FOOTER ĐIỀU KHIỂN SẴN SÀNG / BẮT ĐẦU */}
      <div className="ww-room-footer-controls">
        <div className="ww-footer-left">
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>
            Trạng thái cá nhân: <b>{myPlayer?.isHost ? '👑 Chủ phòng' : myPlayer?.isReady ? '✔️ Đã Sẵn Sàng' : '⏳ Chưa Sẵn Sàng'}</b>
          </p>
          {isHost && !allReady && (
            <span style={{ fontSize: '0.78rem', color: '#ef4444', fontWeight: 'bold', display: 'block', marginTop: '2px' }}>
              {!isEnoughPlayers
                ? `⚠️ Cần tối thiểu ${minPlayers} người chơi (hiện có ${playersList.length})`
                : hasEmptySeats
                ? `⚠️ Cần bỏ ${emptySeatsCount} ghế trống để bắt đầu!`
                : '⚠️ Chờ tất cả người chơi Sẵn Sàng'}
            </span>
          )}
        </div>

        <div className="ww-footer-right">
          {!isHost ? (
            <button
              className={`ww-btn ready-btn ${myPlayer?.isReady ? 'active' : ''}`}
              onClick={toggleReady}
            >
              {myPlayer?.isReady ? '❌ Hủy Sẵn Sàng' : '✔️ Sẵn Sàng Chơi'}
            </button>
          ) : (
            <button
              className="ww-btn start-btn"
              disabled={!allReady}
              onClick={startGame}
              style={{
                opacity: allReady ? 1 : 0.5,
                cursor: allReady ? 'pointer' : 'not-allowed'
              }}
            >
              🚀 Bắt Đầu Ván Game {!allReady && `(${readyCount}/${playersList.length})`}
            </button>
          )}
        </div>
      </div>

      {/* MODAL XEM LỊCH SỬ VÁN VỪA RỒI */}
      {activeModal === 'view_last_logs' && (
        <div className="ww-modal-overlay animate-fade-in">
          <div className="ww-modal-card wide">
            <h3>📜 Lịch Sử Tiến Trình Ván Vừa Rồi</h3>
            
            <div className="ww-grouped-logs-container" style={{ maxHeight: '60vh' }}>
              {Object.entries(groupedLastLogs).map(([dayNum, logs]) => {
                const isExpanded = expandedDays[dayNum] !== false;
                return (
                  <div key={dayNum} className="ww-day-log-group">
                    <button
                      type="button"
                      className="ww-day-group-header"
                      onClick={() => toggleDayExpand(dayNum)}
                    >
                      <span>🗓️ NGÀY {dayNum} ({logs.length} sự kiện)</span>
                      <span>{isExpanded ? '🔽 Thu gọn' : '▶️ Mở xem'}</span>
                    </button>

                    {isExpanded && (
                      <div className="ww-day-logs-list">
                        {logs.map((log) => (
                          <div key={log.id} className="ww-log-item">
                            <span className="ww-log-phase">{log.phase}</span>
                            <p className="ww-log-content">{log.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="ww-modal-actions">
              <button type="button" className="ww-btn primary" onClick={() => setActiveModal(null)}>
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CHỈNH SỬA PHÒNG */}
      {activeModal === 'info' && (
        <div className="ww-modal-overlay">
          <div className="ww-modal-card">
            <h3>🏢 Chỉnh Sửa Thông Tin Phòng</h3>
            <form onSubmit={handleSaveInfo} className="ww-modal-form">
              <label>Tên Phòng:</label>
              <input
                type="text"
                value={editRoomName}
                onChange={(e) => setEditRoomName(e.target.value)}
                className="ww-modal-input"
                required
              />

              <label>Mật khẩu phòng (Bỏ trống nếu mở tự do):</label>
              <input
                type="password"
                placeholder="Nhập mật khẩu mới..."
                value={editPassword}
                onChange={(e) => setEditPassword(e.target.value)}
                className="ww-modal-input"
              />

              <div className="ww-modal-actions">
                <button type="button" className="ww-btn danger" onClick={() => setActiveModal(null)}>Hủy</button>
                <button type="submit" className="ww-btn success">Lưu Thông Tin 💾</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL CHỈNH SỬA LUẬT CHƠI */}
      {activeModal === 'rules' && (
        <div className="ww-modal-overlay">
          <div className="ww-modal-card">
            <h3>⏱️ Chỉnh Sửa Thời Gian & Luật Chơi</h3>
            <form onSubmit={handleSaveRules} className="ww-modal-form">
              <label>Thời gian thảo luận Ban Ngày (giây):</label>
              <select
                value={gameRules.dayDuration}
                onChange={(e) => setGameRules({ ...gameRules, dayDuration: Number(e.target.value) })}
                className="ww-modal-input"
              >
                <option value={0}>Thời gian tự do (Chủ phòng bấm)</option>
                <option value={60}>60 Giây (Nhanh)</option>
                <option value={90}>90 Giây (Tiêu chuẩn)</option>
                <option value={120}>120 Giây (Thoải mái)</option>
                <option value={180}>180 Giây (Dài)</option>
              </select>

              <label>Thời gian hành động Ban Đêm (giây):</label>
              <select
                value={gameRules.nightDuration}
                onChange={(e) => setGameRules({ ...gameRules, nightDuration: Number(e.target.value) })}
                className="ww-modal-input"
              >
                <option value={0}>Thời gian tự do (Chủ phòng bấm)</option>
                <option value={20}>20 Giây</option>
                <option value={30}>30 Giây (Tiêu chuẩn)</option>
                <option value={45}>45 Giây</option>
                <option value={60}>60 Giây</option>
              </select>

              <label>Thời gian Bỏ phiếu / Bào chữa (giây):</label>
              <select
                value={gameRules.voteDuration}
                onChange={(e) => setGameRules({ ...gameRules, voteDuration: Number(e.target.value) })}
                className="ww-modal-input"
              >
                <option value={0}>Thời gian tự do (Chủ phòng bấm)</option>
                <option value={20}>20 Giây</option>
                <option value={30}>30 Giây</option>
                <option value={45}>45 Giây</option>
              </select>

              <label className="ww-checkbox-label">
                <input
                  type="checkbox"
                  checked={gameRules.revealRoleOnDeath}
                  onChange={(e) => setGameRules({ ...gameRules, revealRoleOnDeath: e.target.checked })}
                />
                <span>Công khai vai trò khi người chơi tử vong</span>
              </label>

              <div className="ww-modal-actions">
                <button type="button" className="ww-btn danger" onClick={() => setActiveModal(null)}>Hủy</button>
                <button type="submit" className="ww-btn success">Lưu Luật Chơi 💾</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL CHỈNH SỬA VAI TRÒ */}
      {activeModal === 'roles' && (
        <div className="ww-modal-overlay">
          <div className="ww-modal-card wide">
            <h3>🎭 Cấu Hình Vai Trò Game</h3>
            <form onSubmit={handleSaveRoles} className="ww-modal-form">
              <label>Số lượng Ma Sói:</label>
              <select
                value={rolesConfig.werewolfCount || 2}
                onChange={(e) => setRolesConfig({ ...rolesConfig, werewolfCount: Number(e.target.value) })}
                className="ww-modal-input"
              >
                <option value={1}>1 Ma Sói</option>
                <option value={2}>2 Ma Sói</option>
                <option value={3}>3 Ma Sói</option>
                <option value={4}>4 Ma Sói</option>
              </select>

              <label><b>Bật/Tắt Vai Trò Đặc Biệt:</b></label>
              <div className="ww-roles-checkbox-grid">
                <label className="ww-role-checkbox-item">
                  <input
                    type="checkbox"
                    checked={rolesConfig.seer}
                    onChange={(e) => setRolesConfig({ ...rolesConfig, seer: e.target.checked })}
                  />
                  <span>🔮 Tiên Tri (1)</span>
                </label>

                <label className="ww-role-checkbox-item">
                  <input
                    type="checkbox"
                    checked={rolesConfig.bodyguard}
                    onChange={(e) => setRolesConfig({ ...rolesConfig, bodyguard: e.target.checked })}
                  />
                  <span>🛡️ Bảo Vệ (1)</span>
                </label>

                <label className="ww-role-checkbox-item">
                  <input
                    type="checkbox"
                    checked={rolesConfig.witch}
                    onChange={(e) => setRolesConfig({ ...rolesConfig, witch: e.target.checked })}
                  />
                  <span>🧪 Phù Thủy (1)</span>
                </label>

                <label className="ww-role-checkbox-item">
                  <input
                    type="checkbox"
                    checked={rolesConfig.hunter}
                    onChange={(e) => setRolesConfig({ ...rolesConfig, hunter: e.target.checked })}
                  />
                  <span>🏹 Thợ Săn (1)</span>
                </label>

                <label className="ww-role-checkbox-item">
                  <input
                    type="checkbox"
                    checked={rolesConfig.cupid}
                    onChange={(e) => setRolesConfig({ ...rolesConfig, cupid: e.target.checked })}
                  />
                  <span>💘 Thần Tình Yêu (1)</span>
                </label>
              </div>

              <div className={`ww-role-validation-box ${isOverLimit ? 'error' : 'success'}`}>
                <div className="ww-validation-title">
                  <span>📊 Thống kê phân bổ ({totalRolesAssigned}/{maxP} người):</span>
                  <span className="ww-validation-badge">
                    {isOverLimit ? '⚠️ Vượt quá số người!' : '✅ Khớp số người chơi'}
                  </span>
                </div>
                <div className="ww-validation-breakdown">
                  <span>🐺 Sói: <b>{wolvesCount}</b></span>
                  <span>🔮 Special: <b>{specialRolesCount}</b></span>
                  <span>👨‍🌾 Dân Làng: <b>{calculatedVillagers}</b></span>
                </div>
              </div>

              <div className="ww-modal-actions">
                <button type="button" className="ww-btn danger" onClick={() => setActiveModal(null)}>Hủy</button>
                <button type="submit" className="ww-btn success" disabled={isOverLimit}>Lưu Vai Trò 💾</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL XEM CHI TIẾT LUẬT CHƠI */}
      {activeModal === 'view_rules' && (
        <div className="ww-modal-overlay">
          <div className="ww-modal-card wide">
            <h3>📖 Chi Tiết Luật Chơi & Vai Trò</h3>

            <div className="ww-rules-content">
              <div className="ww-rule-section">
                <h5>⏱️ Thời Gian Các Pha:</h5>
                <p>• Ban Ngày (Thảo luận): <b>{currentRules.dayDuration} giây</b></p>
                <p>• Ban Đêm (Hành động): <b>{currentRules.nightDuration} giây</b></p>
                <p>• Bỏ Phiếu (Bào chữa): <b>{currentRules.voteDuration} giây</b></p>
                <p>• Công khai vai trò khi tử vong: <b>{currentRules.revealRoleOnDeath ? 'CÓ' : 'KHÔNG'}</b></p>
              </div>

              <div className="ww-rule-section">
                <h5>🎭 Danh Sách Vai Trò Trong Ván ({currentRoom.maxPlayers} Người):</h5>
                <ul className="ww-roles-detail-list">
                  <li><b>🐺 Ma Sói ({currentRoles.werewolfCount || 2}):</b> Tỉnh dậy ban đêm chọn cắn 1 người.</li>
                  {currentRoles.seer && <li><b>🔮 Tiên Tri (1):</b> Soi bản phe (Sói/Dân) 1 người mỗi đêm.</li>}
                  {currentRoles.bodyguard && <li><b>🛡️ Bảo Vệ (1):</b> Bảo vệ 1 người khỏi Sói mỗi đêm.</li>}
                  {currentRoles.witch && <li><b>🧪 Phù Thủy (1):</b> Có 1 bình cứu sống và 1 bình độc.</li>}
                  {currentRoles.hunter && <li><b>🏹 Thợ Săn (1):</b> Bắn 1 người theo khi bị loại.</li>}
                  {currentRoles.cupid && <li><b>💘 Thần Tình Yêu (1):</b> Se duyên 2 người chơi thành cặp đôi.</li>}
                  <li><b>👨‍🌾 Dân Làng ({Math.max(0, currentRoom.maxPlayers - (currentRoles.werewolfCount || 2) - ((currentRoles.seer ? 1 : 0) + (currentRoles.bodyguard ? 1 : 0) + (currentRoles.witch ? 1 : 0) + (currentRoles.hunter ? 1 : 0) + (currentRoles.cupid ? 1 : 0)))}):</b> Lập luận và bỏ phiếu ban ngày.</li>
                </ul>
              </div>
            </div>

            <div className="ww-modal-actions">
              <button type="button" className="ww-btn primary" onClick={() => setActiveModal(null)}>Đã Hiểu</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WerewolfRoomView;